<template>
  <div class="refinement tags">
    <div class="refinement-header">
      <div class="title">
        <i class="material-symbols-outlined fill" aria-hidden="true" data-icon="label"></i>
      </div>
      <div class="tag-search">
        <div class="inner">
          <callout v-if="error" type="error">
            <div>{{ error }}</div>
          </callout>
          <multiselect
            v-if="!error"
            :model-value="dropdownFilter.value"
            :options="userTags"
            :multiple="true"
            select-label="Select"
            deselect-label="Remove"
            placeholder="Search by tags"
            open-direction="bottom"
            @select="toggleDropdownValue"
            @remove="toggleDropdownValue"
          >
            <!-- custom tags -->
            <template #tag="{ option, remove }">
              <tag-button
                :label="option"
                :aria-label="`Remove ${option}`"
                @click-action="remove(option)"
              />
            </template>
          </multiselect>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, watch } from 'vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { useSearchStore } from '@/stores/search'
import Multiselect from 'vue-multiselect'
import _debounce from 'lodash/debounce'
import { fbApi, objToSearchParams } from '@/utils/http-api'
import Callout from '@/components/notifications/callout.vue'
import TagButton from '@/components/buttons/tag-button.vue'
import type { DropdownFilter, EntityType, SearchTag } from '@/types/search-types'

/** PROPS **/
const props = withDefaults(
  defineProps<{
    entityType?: EntityType
    entityCategory?: '' | 'recipe'
    dropdownFilter: DropdownFilter
  }>(),
  {
    entityType: 'workout',
    entityCategory: '',
  },
)

/** NON REACTIVE **/
const touchedTagValues = new Set<string>()

/** EMITS **/
const emit = defineEmits(['dropdown-change'])

/** STATE **/
const userAuthStore = useUserAuthStore()
const searchStore = useSearchStore()
const userTags = ref<string[]>([])
const error = ref('')

/** WATCHERS **/
// TODO: refactor user tags to live in user store and get ride of watchers, should work like favorites
watch(
  () => userAuthStore.refreshTagsInstances,
  (newVal: boolean, oldVal: boolean) => {
    if (newVal !== oldVal && newVal && userAuthStore.features.TAGS) {
      fetchUserTags()
      userAuthStore.setRefreshTagsInstances(false)
    }
  },
)

/** LIFECYCLE **/
onBeforeMount(async () => {
  // get the initial tag list for the user (if they have access to the feature)
  // otherwise this query will fail
  if (userAuthStore.features.TAGS) {
    await fetchUserTags()
  }
})

/** METHODS **/
async function fetchUserTags() {
  error.value = ''

  try {
    const res = await fbApi.get(`/tags/unique`, {
      searchParams: objToSearchParams({
        taggableType: props.entityType,
        category: props.entityCategory,
      }),
    })

    // make sure both are returned okay
    if (!res || !res.body || !res.body.data) {
      throw Error('missing tag response data')
    }

    // these are distinct user tags
    userTags.value = res.body.data.tags
  } catch (e) {
    error.value = 'Unable to load tags'
  }
}

function toggleDropdownValue(tagName: string) {
  // pushed to touched for debounced tags
  touchedTagValues.add(tagName)

  // toggle the value
  if (props.dropdownFilter.value.includes(tagName)) {
    // remove tag
    searchStore.clearDropdown({
      filterName: props.dropdownFilter.name,
      value: tagName,
    })
  } else {
    // add tag
    searchStore.setDropdownValue({
      filterName: props.dropdownFilter.name,
      value: tagName,
    })
  }

  // debounce adding tags and emitting
  debounceChange()
}

// emit all the IDs that changed
const debounceChange = _debounce(() => {
  const tags: SearchTag[] = []

  touchedTagValues.forEach((tagName) => {
    tags.push(
      searchStore.dropdownTagItem({
        id: `${props.dropdownFilter.name}_${tagName}`,
        filterName: props.dropdownFilter.name,
        tagLabel: tagName,
        value: tagName,
      }),
    )
  })

  touchedTagValues.clear()

  // push tags to parent
  emit('dropdown-change', tags)
}, 500)
</script>
